import type { CustomRoute, ElegantConstRoute } from '@elegant-router/types';
import { generatedRoutes } from '../elegant/routes';
import { layouts, views } from '../elegant/imports';
import { transformElegantRoutesToVueRoutes } from '../elegant/transform';
import { filterHiddenRoutes, warnHiddenRoutes } from './hidden-routes.config';

/**
 * custom routes
 *
 * @link https://github.com/soybeanjs/elegant-router?tab=readme-ov-file#custom-route
 */
const customRoutes: CustomRoute[] = [
  // {
  //   name: 'exception',
  //   path: '/exception',
  //   component: 'layout.base',
  //   meta: {
  //     title: 'exception',
  //     i18nKey: 'route.exception',
  //     icon: 'ant-design:exception-outlined',
  //     order: 7
  //   },
  //   children: [
  //     {
  //       name: 'exception_403',
  //       path: '/exception/403',
  //       component: 'view.403',
  //       meta: {
  //         title: 'exception_403',
  //         i18nKey: 'route.exception_403',
  //         icon: 'ic:baseline-block'
  //       }
  //     },
  //     {
  //       name: 'exception_404',
  //       path: '/exception/404',
  //       component: 'view.404',
  //       meta: {
  //         title: 'exception_404',
  //         i18nKey: 'route.exception_404',
  //         icon: 'ic:baseline-web-asset-off'
  //       }
  //     },
  //     {
  //       name: 'exception_500',
  //       path: '/exception/500',
  //       component: 'view.500',
  //       meta: {
  //         title: 'exception_500',
  //         i18nKey: 'route.exception_500',
  //         icon: 'ic:baseline-wifi-off'
  //       }
  //     }
  //   ]
  // }
];

const BUILTIN_MANAGED_ROUTE_NAMES = new Set<string>(['403']);

/**
 * Public cost tools share one sidebar category. The calculator and tokenizer are
 * generated as single-level constant routes (their public paths and tests depend
 * on that), so instead of moving the view folders we re-parent the generated
 * records under a custom `cost-tools` layout route at composition time. The
 * children keep their own names, paths, view components and `constant` meta, so
 * every existing deep link (`/calculator?model=…`, `/tokenizer`) still resolves.
 */
const COST_TOOL_ROUTE_NAMES = ['calculator', 'tokenizer'] as const;

function groupCostToolRoutes(routes: ElegantConstRoute[]): ElegantConstRoute[] {
  const tools = routes.filter(route => (COST_TOOL_ROUTE_NAMES as readonly string[]).includes(route.name));
  if (tools.length !== COST_TOOL_ROUTE_NAMES.length) return routes;

  const children: ElegantConstRoute[] = COST_TOOL_ROUTE_NAMES.map(name => {
    const tool = tools.find(route => route.name === name)!;
    return {
      ...tool,
      // elegant-router nests a child only when its name is `parent_child`; the public path is unchanged.
      name: `cost-tools_${name}`,
      // `layout.base$view.x` → `view.x`: the parent supplies the layout now.
      component: `view.${name}`,
      meta: { ...tool.meta, title: `cost-tools_${name}`, i18nKey: `route.cost-tools_${name}`, order: name === 'calculator' ? 1 : 2 }
    };
  });

  const group: ElegantConstRoute = {
    name: 'cost-tools',
    path: '/cost-tools',
    component: 'layout.base',
    redirect: { name: 'cost-tools_calculator' },
    meta: {
      title: 'cost-tools',
      icon: 'icon-park-outline:toolkit',
      i18nKey: 'route.cost-tools',
      // Between Model Studio (2.5) and Billing (3), where the two tools used to sit.
      order: 2.8,
      constant: true
    },
    children
  };

  const firstToolIndex = routes.findIndex(route => (COST_TOOL_ROUTE_NAMES as readonly string[]).includes(route.name));
  const rest = routes.filter(route => !(COST_TOOL_ROUTE_NAMES as readonly string[]).includes(route.name));
  rest.splice(firstToolIndex, 0, group);
  return rest;
}

function getPublicConstantChildRoutes(item: ElegantConstRoute): ElegantConstRoute[] {
  if (!item.children?.length) return [];

  return item.children.flatMap(child => {
    if (!child.meta?.constant) return [];

    return [
      {
        name: `${child.name.replaceAll('_', '-')}-public-shell`,
        path: child.path,
        component: item.component,
        meta: {
          ...item.meta,
          constant: true,
          hideInMenu: true,
          title: child.meta?.title ?? item.meta?.title,
          i18nKey: child.meta?.i18nKey ?? item.meta?.i18nKey
        },
        children: [
          {
            ...child,
            path: ''
          }
        ]
      }
    ];
  });
}

function withoutPublicConstantChildren(item: ElegantConstRoute): ElegantConstRoute | null {
  if (item.meta?.constant) return null;

  if (!item.children?.length) return item;

  const children = item.children.filter(child => !child.meta?.constant);

  return {
    ...item,
    children
  };
}

/**
 * The sponsorship apply page (`/sponsorship/apply`) is generated as a public
 * route by `build/plugins/router.ts` so the WordPress CTA can deep-link directly
 * into the inline phone-OTP wizard. The sidebar parent route (`/sponsorship`)
 * should still land on the hub, not the hidden apply modal.
 */
function passthrough(item: ElegantConstRoute): ElegantConstRoute | null {
  if (item.name === 'sponsorship') {
    return {
      ...item,
      redirect: { name: 'sponsorship_home' }
    };
  }

  if (item.name === 'support') {
    return {
      ...item,
      redirect: { name: 'support_dashboard' }
    };
  }

  return item;
}

/** create routes when the auth route mode is static */
export function createStaticRoutes() {
  const constantRoutes: ElegantConstRoute[] = [];

  const authRoutes: ElegantConstRoute[] = [];

  // Remove work-in-progress routes/views that are gated for this environment
  // (see src/router/routes/hidden-routes.config.ts). Filtering here means both
  // the vue-router records AND the sidebar menu — which are derived from the
  // same route table — never see the hidden routes.
  const sourceRoutes = groupCostToolRoutes([...customRoutes, ...generatedRoutes] as ElegantConstRoute[]);
  const { routes: visibleRoutes, report } = filterHiddenRoutes(sourceRoutes);

  warnHiddenRoutes(report);

  visibleRoutes.forEach(route => {
    const item = route as ElegantConstRoute;

    if (BUILTIN_MANAGED_ROUTE_NAMES.has(item.name)) {
      return;
    }

    if (item.meta?.constant) {
      constantRoutes.push(item);
      return;
    }

    constantRoutes.push(...getPublicConstantChildRoutes(item));

    const authItem = withoutPublicConstantChildren(item);
    if (!authItem) return;

    const remaining = passthrough(authItem);
    if (remaining) {
      authRoutes.push(remaining);
    }
  });

  return {
    constantRoutes,
    authRoutes
  };
}

/**
 * View components for the grouped cost-tool children. `imports.ts` is generated from the
 * `src/views` tree and only knows `calculator` / `tokenizer`; the composed children resolve
 * as `view.cost-tools_*`, so alias them to the same lazy views here.
 */
const groupedViews: Record<string, (typeof views)[keyof typeof views]> = {
  'cost-tools_calculator': views.calculator,
  'cost-tools_tokenizer': views.tokenizer
};

/**
 * Get auth vue routes
 *
 * @param routes Elegant routes
 */
export function getAuthVueRoutes(routes: ElegantConstRoute[]) {
  return transformElegantRoutesToVueRoutes(routes, layouts, { ...views, ...groupedViews });
}
